import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/react";
import { PostHogProvider } from "@posthog/react";
import {
  initSentry,
  tracePropagationTargets,
} from "@sill/shared/observability/sentry";
import { AppErrorBoundary } from "@sill/shared/observability/AppErrorBoundary";
import { ToastProvider } from "./components/ui/toast";
import { App } from "./App";
import { getBrowserPathname, resolveClerkBypass } from "./lib/dev-preview";
import { consumerPostHogOptions } from "./lib/posthog-options";
import {
  clerkOriginsFromPublishableKey,
  originOf,
  resolveClerkNavigation,
} from "./lib/clerk-navigation";

initSentry({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENV ?? "local",
  release: import.meta.env.VITE_GIT_SHA ?? "dev",
  tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  apiHosts: tracePropagationTargets(import.meta.env.VITE_API_URL),
});
// Brand typeface: Instrument Sans (text/UI/figures) + Fraunces (display, with
// the opsz axis) + JetBrains Mono (mono figures).
import "@fontsource-variable/instrument-sans/index.css";
import "@fontsource-variable/fraunces/opsz.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/700.css";
import "./index.css";

const CLERK_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const CLERK_SIGN_IN_URL = import.meta.env.VITE_CLERK_SIGN_IN_URL || "/login";
const CLERK_SIGN_UP_URL = import.meta.env.VITE_CLERK_SIGN_UP_URL || "/onboarding";

// The only origins a Clerk redirect may hard-navigate to (SEC-59): the Pro and
// marketing apps, and the Clerk hosts the publishable key names. A sign-in or
// sign-up URL configured as an absolute URL is trusted too.
const CLERK_REDIRECT_ORIGINS = [
  originOf(import.meta.env.VITE_PRO_URL || "https://pro.usesill.com"),
  originOf(import.meta.env.VITE_MARKETING_URL || "https://usesill.com"),
  originOf(CLERK_SIGN_IN_URL),
  originOf(CLERK_SIGN_UP_URL),
  ...clerkOriginsFromPublishableKey(CLERK_KEY),
].filter((o): o is string => o !== null);
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";
const pathname = getBrowserPathname();
// resolveClerkBypass is the single source of truth for the bypass decision.
// previewRouteUsesClerk (same module) is the allowlist; DashboardShell imports
// it too so the two callsites stay in sync.
const shouldBypassClerk = resolveClerkBypass(pathname, !!CLERK_KEY);
const clerkAppearance = {
  cssLayerName: "clerk",
  elements: {
    card: "w-full",
    cardBox:
      "w-full max-w-[28rem] !static !inset-auto !m-0 !max-h-[calc(100dvh-2rem)] !overflow-y-auto !transform-none",
    modalBackdrop: "flex min-h-dvh items-center justify-center p-4",
    modalContent:
      "w-full max-w-[28rem] !static !inset-auto !m-0 !max-h-[calc(100dvh-2rem)] !overflow-y-auto !transform-none",
    rootBox: "flex min-h-dvh items-center justify-center p-4 sm:p-6",
  },
} as const;

if (!CLERK_KEY && !shouldBypassClerk) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

// Clerk drives its own auth redirects (sign-in, sign-up, post-signup
// `forceRedirectUrl`). Without router integration it falls back to
// `window.location`, hard-reloading the whole SPA — that reload is what makes
// `/pros/welcome` flash the generic shell loader after signup. Wiring Clerk to
// React Router keeps same-origin redirects client-side, while cross-origin auth
// redirects still hard-navigate so none are silently dropped.
function ClerkWithRouter({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const go = (to: string, replace: boolean) => {
    const target = resolveClerkNavigation(
      to,
      window.location.origin,
      CLERK_REDIRECT_ORIGINS,
    );
    if (target.kind === "external") {
      window.location.assign(target.url);
      return;
    }
    navigate(target.path, { replace });
  };

  return (
    <ClerkProvider
      appearance={clerkAppearance}
      publishableKey={CLERK_KEY}
      signInUrl={CLERK_SIGN_IN_URL}
      signUpUrl={CLERK_SIGN_UP_URL}
      routerPush={(to) => go(to, false)}
      routerReplace={(to) => go(to, true)}
    >
      {children}
    </ClerkProvider>
  );
}

const appShell = POSTHOG_KEY ? (
  <PostHogProvider
    apiKey={POSTHOG_KEY}
    options={consumerPostHogOptions(POSTHOG_HOST)}
  >
    <ToastProvider>
      <App />
    </ToastProvider>
  </PostHogProvider>
) : (
  <ToastProvider>
    <App />
  </ToastProvider>
);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <AppErrorBoundary>
        {shouldBypassClerk ? (
          appShell
        ) : (
          <ClerkWithRouter>{appShell}</ClerkWithRouter>
        )}
      </AppErrorBoundary>
    </BrowserRouter>
  </StrictMode>,
);
